(() => {
  const revealSelector = '[data-teka-service-reveal]';
  const motionTargetsSelector = `${revealSelector}, [data-teka-service-draw], [data-teka-specialist-item]`;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;
  let specialistObserver;
  let sectorObserver;
  let animationFrame = 0;

  const isElementRoot = (root) => root instanceof Element || root instanceof Document;

  const getMatches = (root, selector) => {
    if (!isElementRoot(root)) return [];
    const matches = [];
    if (root instanceof Element && root.matches(selector)) matches.push(root);
    matches.push(...root.querySelectorAll(selector));
    return matches;
  };

  const getTargets = (root) => getMatches(root, motionTargetsSelector);

  const splitHeroHeading = (heading) => {
    if (heading.dataset.tekaWordsReady === 'true') return;
    const words = heading.textContent.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return;

    const fragment = document.createDocumentFragment();
    words.forEach((word, index) => {
      const outer = document.createElement('span');
      const inner = document.createElement('span');
      outer.className = 'teka-service-word';
      inner.textContent = word;
      inner.style.transitionDelay = `${Math.min(index * 55, 495)}ms`;
      outer.append(inner);
      fragment.append(outer);

      if (index < words.length - 1) {
        if (heading.hasAttribute('data-teka-service-lines') && /[.!?]$/.test(word)) {
          fragment.append(document.createElement('br'));
        } else {
          fragment.append(document.createTextNode(' '));
        }
      }
    });

    heading.replaceChildren(fragment);
    heading.dataset.tekaWordsReady = 'true';
  };

  const activateSpecialist = (item) => {
    const section = item.closest('[data-teka-specialists]');
    if (!section) return;

    const items = [...section.querySelectorAll('[data-teka-specialist-item]')];
    const images = [...section.querySelectorAll('.stage .teka-specialist-stage-image, .stage .teka-specialist-stage-fallback')];
    const index = Number(item.dataset.k);
    if (!Number.isInteger(index) || index < 0 || index >= items.length) return;

    items.forEach((entry, entryIndex) => {
      const active = entryIndex === index;
      entry.classList.toggle('on', active);
      entry.setAttribute('aria-current', active ? 'step' : 'false');
    });

    const previousIndex = Number(section.dataset.tekaActiveSpecialist ?? items.find((entry) => entry.classList.contains('on'))?.dataset.k ?? 0);
    images.forEach((image, imageIndex) => {
      image.classList.toggle('prev', imageIndex === previousIndex && index !== previousIndex);
      image.classList.toggle('on', imageIndex === index);
    });

    section.dataset.tekaActiveSpecialist = String(index);
    section.querySelector('[data-teka-specialist-count]')?.replaceChildren(String(index + 1).padStart(2, '0'));
    section.querySelector('[data-teka-specialist-progress]')?.parentElement?.style.setProperty('--pg', String((index + 1) / items.length));
  };

  const setupSpecialistControls = (root) => {
    getMatches(root, '[data-teka-specialists]').forEach((section) => {
      if (section.dataset.tekaSpecialistsReady === 'true') return;
      section.dataset.tekaSpecialistsReady = 'true';
      if (section.dataset.tekaActiveSpecialist === undefined) {
        section.dataset.tekaActiveSpecialist = section.querySelector('[data-teka-specialist-item].on')?.dataset.k || '0';
      }

      const handleClick = (event) => {
        const item = event.target.closest('[data-teka-specialist-item]');
        if (item && section.contains(item)) activateSpecialist(item);
      };
      const handleKeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const item = event.target.closest('[data-teka-specialist-item]');
        if (!item || !section.contains(item)) return;
        event.preventDefault();
        activateSpecialist(item);
      };

      section.addEventListener('click', handleClick);
      section.addEventListener('keydown', handleKeydown);
      if (!reducedMotion.matches && 'IntersectionObserver' in window) {
        specialistObserver ||= new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || !window.matchMedia('(min-width: 1001px)').matches) return;
            activateSpecialist(entry.target);
          });
        }, { rootMargin: '-45% 0px -45% 0px' });
        section.querySelectorAll('[data-teka-specialist-item]').forEach((item) => specialistObserver.observe(item));
      }
      section.__tekaSpecialistCleanup = () => {
        section.removeEventListener('click', handleClick);
        section.removeEventListener('keydown', handleKeydown);
        section.querySelectorAll('[data-teka-specialist-item]').forEach((item) => specialistObserver?.unobserve(item));
        delete section.dataset.tekaSpecialistsReady;
      };
    });
  };

  const setupSectorPreviews = (root) => {
    getMatches(root, '[data-teka-sectors]').forEach((section) => {
      if (section.dataset.tekaSectorPreviewReady === 'true') return;
      section.dataset.tekaSectorPreviewReady = 'true';
      const floating = section.querySelector('[data-teka-sector-float]');
      const floatingImage = section.querySelector('[data-teka-sector-float-image]');
      if (!floating || !floatingImage) return;
      const rows = [...section.querySelectorAll('[data-teka-sector-row]')];
      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        rows.forEach((row) => row.classList.add('is-visible'));
      } else {
        sectorObserver ||= new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            sectorObserver.unobserve(entry.target);
          });
        }, { threshold: .6, rootMargin: '0px 0px -8% 0px' });
        rows.forEach((row) => {
          if (row.dataset.tekaSectorObserved === 'true') return;
          row.dataset.tekaSectorObserved = 'true';
          sectorObserver.observe(row);
        });
      }

      const update = (event) => {
        const row = event.target.closest('[data-teka-sector-row]');
        if (!row || !section.contains(row) || window.matchMedia('(max-width: 1000px)').matches) return;
        const thumbnail = row.querySelector('.th');
        if (!thumbnail?.currentSrc && !thumbnail?.src) return;
        floatingImage.src = thumbnail.currentSrc || thumbnail.src;
        const bounds = section.getBoundingClientRect();
        floating.style.left = `${event.clientX}px`;
        floating.style.top = `${Math.max(bounds.top + 190, event.clientY - 190)}px`;
        floating.classList.add('is-visible');
        row.classList.add('is-active');
      };
      const hide = (event) => {
        if (event.relatedTarget && section.contains(event.relatedTarget)) return;
        floating.classList.remove('is-visible');
        section.querySelectorAll('[data-teka-sector-row].is-active').forEach((row) => row.classList.remove('is-active'));
      };
      const handleKeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const row = event.target.closest('[data-teka-sector-row]');
        if (!row || !section.contains(row)) return;
        event.preventDefault();
        section.querySelectorAll('[data-teka-sector-row].is-active').forEach((active) => active.classList.remove('is-active'));
        row.classList.add('is-active');
      };
      section.addEventListener('pointerover', update);
      section.addEventListener('pointermove', update);
      section.addEventListener('pointerleave', hide);
      section.addEventListener('keydown', handleKeydown);
      section.__tekaSectorCleanup = () => {
        section.removeEventListener('pointerover', update);
        section.removeEventListener('pointermove', update);
        section.removeEventListener('pointerleave', hide);
        section.removeEventListener('keydown', handleKeydown);
        rows.forEach((row) => {
          sectorObserver?.unobserve(row);
          delete row.dataset.tekaSectorObserved;
        });
        delete section.dataset.tekaSectorPreviewReady;
      };
    });
  };

  const setupGlowHandlers = (root) => {
    getMatches(root, '[data-teka-service-glow]').forEach((glow) => {
      if (glow.dataset.tekaGlowReady === 'true') return;
      glow.dataset.tekaGlowReady = 'true';
      const move = (event) => {
        const bounds = glow.getBoundingClientRect();
        glow.style.setProperty('--teka-glow-x', `${event.clientX - bounds.left}px`);
        glow.style.setProperty('--teka-glow-y', `${event.clientY - bounds.top}px`);
      };
      glow.addEventListener('pointermove', move, { passive: true });
      glow.__tekaGlowCleanup = () => {
        glow.removeEventListener('pointermove', move);
        delete glow.dataset.tekaGlowReady;
      };
    });
  };

  const initialize = (root = document) => {
    if (!isElementRoot(root)) return;
    const targets = getTargets(root);

    getMatches(root, '[data-teka-service-words]').forEach(splitHeroHeading);
    getMatches(root, '.teka-service-page').forEach((section) => {
      [...section.querySelectorAll('[data-teka-service-reveal].fade')].forEach((element, index) => {
        element.style.transitionDelay = `${Math.min(120 + index * 70, 540)}ms`;
      });
    });
    setupSpecialistControls(root);
    setupSectorPreviews(root);
    setupGlowHandlers(root);

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((target) => {
        if (target.matches(revealSelector)) target.classList.add('is-visible');
        if (target.matches('[data-teka-service-draw]')) target.classList.add('is-drawn');
        if (target.matches('[data-teka-specialist-item]')) target.classList.add('is-seen');
      });
      getMatches(root, '[data-teka-specialists]').forEach((section) => {
        const first = section.querySelector('[data-teka-specialist-item]');
        if (first) activateSpecialist(first);
      });
      getMatches(root, '[data-teka-sector-row]').forEach((row) => row.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const target = entry.target;
          if (target.matches(revealSelector)) {
            target.classList.add('is-visible');
            revealObserver.unobserve(target);
          }
          if (target.matches('[data-teka-service-draw]')) {
            target.classList.add('is-drawn');
            revealObserver.unobserve(target);
          }
          if (target.matches('[data-teka-sector-row]')) {
            target.classList.add('is-visible');
          }
          if (target.matches('[data-teka-specialist-item]')) {
            target.classList.add('is-seen');
          }
        });
      }, { threshold: 0.18 });
    }

    targets.forEach((target) => {
      if (target.dataset.tekaServiceObserved === 'true') return;
      target.dataset.tekaServiceObserved = 'true';
      revealObserver.observe(target);
    });
  };

  const getScrollContainer = () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return window;
    const overflowY = getComputedStyle(wrapper).overflowY;
    return ['auto', 'scroll', 'overlay'].includes(overflowY) && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : window;
  };

  const updateScrollEffects = () => {
    animationFrame = 0;
    if (reducedMotion.matches) return;
    const viewportHeight = window.innerHeight;

    document.querySelectorAll('.teka-service-page--hero').forEach((hero) => {
      const media = hero.querySelector('.teka-service-hero__media');
      if (!media) return;
      const bounds = media.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (viewportHeight - bounds.top) / (viewportHeight * .9)));
      hero.style.setProperty('--teka-hero-inset', `${7 * (1 - progress)}%`);
    });

    document.querySelectorAll('[data-teka-service-parallax]').forEach((layer) => {
      const section = layer.closest('.teka-service-page');
      if (!section) return;
      const bounds = section.getBoundingClientRect();
      const centerDistance = bounds.top + bounds.height / 2 - viewportHeight / 2;
      layer.style.setProperty('--teka-service-parallax-y', `${Math.max(-36, Math.min(36, centerDistance * -.07))}px`);
      const proofImage = layer.matches('.teka-service-proof__image-wrap') ? layer.querySelector('img') : null;
      if (proofImage) {
        const imageBounds = layer.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, (viewportHeight - imageBounds.top) / (viewportHeight + imageBounds.height)));
        proofImage.style.setProperty('--teka-proof-scale', (1.15 - .15 * progress).toFixed(3));
      }
    });

    document.querySelectorAll('[data-teka-service-glow]').forEach((glow) => {
      const bounds = glow.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (viewportHeight - bounds.top) / (viewportHeight + bounds.height)));
      glow.style.setProperty('--teka-glow-scale', (1.15 - .15 * progress).toFixed(3));
    });
  };

  const scheduleScrollEffects = () => {
    if (animationFrame || reducedMotion.matches) return;
    animationFrame = requestAnimationFrame(updateScrollEffects);
  };

  const cleanup = (root) => {
    if (!(root instanceof Element)) return;
    getTargets(root).forEach((target) => {
      revealObserver?.unobserve(target);
      delete target.dataset.tekaServiceObserved;
    });
    getMatches(root, '[data-teka-specialists]').forEach((section) => section.__tekaSpecialistCleanup?.());
    getMatches(root, '[data-teka-sectors]').forEach((section) => section.__tekaSectorCleanup?.());
    getMatches(root, '[data-teka-service-glow]').forEach((glow) => glow.__tekaGlowCleanup?.());
  };

  document.documentElement.classList.add('js');
  initialize();
  updateScrollEffects();

  const scrollContainer = getScrollContainer();
  window.addEventListener('scroll', scheduleScrollEffects, { passive: true });
  if (scrollContainer !== window) scrollContainer.addEventListener('scroll', scheduleScrollEffects, { passive: true });
  window.addEventListener('resize', scheduleScrollEffects, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    scheduleScrollEffects();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    cleanup(event.target);
    scheduleScrollEffects();
  });

  reducedMotion.addEventListener?.('change', (event) => {
    if (event.matches) {
      getMatches(document, revealSelector).forEach((target) => target.classList.add('is-visible'));
      getMatches(document, '[data-teka-service-draw]').forEach((target) => target.classList.add('is-drawn'));
      getMatches(document, '[data-teka-sector-row]').forEach((target) => target.classList.add('is-visible', 'is-active'));
      document.querySelectorAll('[data-teka-service-parallax]').forEach((layer) => layer.style.removeProperty('--teka-service-parallax-y'));
    } else {
      initialize(document);
      scheduleScrollEffects();
    }
  });
})();
